import { z } from "zod";

import type { SystemMessageToDisplayInSystemMessageComponent } from "@js/apps/messenger/components/system-message/system-message-component";
import { typeGuard } from "@js/utils";

import type { User } from "./auth";
import type { LinkMetadata } from "./common";
import type { PaginatedResult } from "./generic";
import type { Attachment } from "./uploads";

export type Message = UserMessage | SystemMessage;

export type BaseMessage = {
  id: number;
  room: number;
  created: string;
  sent: string;
  optimistic_id?: string;
  links_metadata: LinkMetadata[];
  last_message_content?: string;
  is_spam: boolean;
};

export type MessageAttachment = {
  id: number;
  link: string;
  safe_file_name: string;
  thumbnail: string | null;
  attachment: Omit<Attachment, "thumbnail">;
};

type BaseUserMessage = BaseMessage & {
  author: MessageAuthor;
  content: string | null;
  calendar_link: string | null;
  system_message_active: false;
  system_message_target: null;
  attachments: MessageAttachment[] | null;
  is_spam: boolean;
};

export type UserMessage = BaseUserMessage & {
  message_type: typeof ENUMS.MessageType.USER_MESSAGE;
};

export type BaseSystemMessage = BaseMessage & {
  author: null;
  content: null;
  calendar_link: null;
  system_message_active: boolean;
};

export type UserSystemMessage =
  | OfferExtendedSystemMessage
  | MessageReportedSystemMessage
  | UserReportedSystemMessage
  | CancelReportsSystemMessage;

export type SystemMessage = UserSystemMessage;

export type OfferExtendedSystemMessage = BaseUserMessage & {
  message_type: typeof ENUMS.MessageType.OFFER_EXTENDED;
  system_message_target: null;
  message_context: {
    job_id: number;
    offer_id: number;
  };
};

export type MessageReportedSystemMessage = BaseSystemMessage & {
  message_type: typeof ENUMS.MessageType.MESSAGE_REPORTED;
  system_message_target: z.infer<typeof ExpectedMessageReportedResults>;
};

export type UserReportedSystemMessage = BaseSystemMessage & {
  message_type: typeof ENUMS.MessageType.USER_REPORTED;
  system_message_target: z.infer<typeof ExpectedUserReportedResults>;
};

export type CancelReportsSystemMessage = BaseSystemMessage & {
  message_type: typeof ENUMS.MessageType.USER_REPORTS_CANCELLED;
  system_message_target: z.infer<typeof ExpectedReportsCancelledResults>;
};

const ExpectedReportedCommonResults = z.object({ id: z.number() });

export const ExpectedMessageReportedResults =
  ExpectedReportedCommonResults.extend({
    author: z.object({ public_name: z.string() }),
  });

export const ExpectedUserReportedResults = ExpectedReportedCommonResults.extend(
  {
    public_name: z.string(),
    first_name: z.string(),
    last_name: z.string().nullable(),
  },
);

export const ExpectedReportsCancelledResults =
  ExpectedReportedCommonResults.extend({
    public_name: z.string(),
  });

export type RoomMessagesPaginatedResult = Pick<
  PaginatedResult<Message>,
  "results" | "next" | "previous"
>;

type BroadcastType = EnumType<typeof ENUMS.BroadcastType>;

export type MessengerEvent<TB extends BroadcastType = BroadcastType> = {
  broadcast_type: TB;
};

export type MessageSpamStatusEvent = MessengerEvent<
  typeof ENUMS.BroadcastType.MESSAGE_SPAM_STATUS
> & {
  message_id: number;
  room_id: number;
  is_marked_as_spam: boolean;
};

export type MessengerMessageEvent = MessengerEvent<
  typeof ENUMS.BroadcastType.MESSAGE
> & {
  message: UserMessage;
};

export type CalendarLinkPromptShowEvent = MessengerEvent<
  typeof ENUMS.BroadcastType.CALENDAR_LINK_PROMPT_SHOWN
> & {
  room_id: number;
};

export type MessageMarkAsReacEvent = MessengerEvent<
  typeof ENUMS.BroadcastType.MESSAGE_MARK_AS_READ
> & {
  room_id: number;
  messages_count: number;
};

export type RoomMessageEvent =
  | MessageSpamStatusEvent
  | MessengerMessageEvent
  | CalendarLinkPromptShowEvent
  | MessageMarkAsReacEvent;

export type GetRoomMessagesReturnType = RoomMessagesPaginatedResult;

export type GetRoomMessagesParamsType = {
  room?: number;
  message_content?: string;
  optimistic_id?: string;
  cursor?: string;
};

export type GetUnreadMessagesCountReturnType = {
  unread_messages_count: number;
};

export type GetRoomListReturnType = PaginatedResult<MessageRoom>;

export type GetRoomListParamsType = {
  name?: string;
  page?: number;
};

export type GetRoomReturnType = MessageRoom;

export type GetRoomParamType = {
  id: number;
};

export const isMessageEvent = (event: RoomMessageEvent) => {
  return (
    typeGuard<object, { message: UserMessage }>(event, "message") &&
    !!event.message
  );
};

export const shouldDisplaySystemMessageComponent = (
  message: Message,
): message is SystemMessageToDisplayInSystemMessageComponent => {
  return !!message.system_message_target;
};

export const isUserMessage = (message: Message): message is UserMessage => {
  return (
    !message.system_message_target &&
    message.message_type === ENUMS.MessageType.USER_MESSAGE
  );
};
export type MessageAuthor = Pick<
  User,
  | "id"
  | "first_name"
  | "last_name"
  | "public_name"
  | "gravatar"
  | "freelancer_approved"
  | "profile_url"
  | "account_type"
  | "title"
  | "created"
  | "has_avatar_set"
  | "avatar"
  | "avatar_thumbnail"
>;

export type MessageRoom = {
  id: number;
  created: string;
  participants: Participant[];
  interlocutors: number[];
  content_type: string;
  name: string;
  unread_message_count: number;
  should_show_calendar_link_prompt: boolean;
  show_contact_support: boolean | null;
  is_last_message_spam: boolean;
  last_message_id: number | null;
  last_message_content: string;
  last_message_timestamp: string | null;
  last_message_author_id: number;
  is_talent_hired_by_client: boolean | null;
};

type InitiatorId = Participant["id"];
type InterlocutorId = Participant["id"];

export type MessageRoomContext = {
  participants: [InitiatorId, InterlocutorId];
  initiated_by_application_reject_request?: boolean;
};

export type GetOrCreateMessageRoomParams = {
  context: MessageRoomContext;
  jobId?: number;
  contentType?: EnumType<typeof ENUMS.RoomTypeContentType>;
};

export type Participant = Pick<
  User,
  | "id"
  | "first_name"
  | "last_name"
  | "public_name"
  | "gravatar"
  | "freelancer_approved"
  | "profile_url"
  | "account_type"
  | "title"
  | "created"
  | "has_avatar_set"
  | "avatar"
  | "avatar_thumbnail"
  | "location"
  | "timezone_abbreviation"
  | "timezone_offset"
>;

export type ReportUserMessageQueryArgs = ReportUserQueryArgs & {
  room?: number;
};

export type ReportUserQueryArgs = {
  id: number;
  reason: string;
  message_to_moderator?: string;
};

export type ReportUserServerResponse = {
  reason: string;
  message?: string;
  message_to_moderator?: string;
};

export type Author = {
  id: number;
  first_name: string;
  last_name: string;
  public_name: string;
  gravatar: string;
  freelancer_approved: boolean;
  agency: number;
  profile_url: string;
  account_type: string;
  title: string;
  created: string;
  has_agency_avatar_set: boolean;
  has_avatar_set: boolean;
  avatar: string;
  avatar_thumbnail: string;
};

export type MessageFileAttachmentData = {
  fileId: string;
  name: string;
  size: number;
  isImage: boolean;
  isLoading: boolean;
  id?: number;
  attachment?: Omit<Attachment, "thumbnail">;
};

export type UnmarkMessageAsSpamQueryArgs = {
  messageId: number;
  room: number;
};
